<template>
	<div id="customer-badge" class="flex w-full flex-row justify-center py-1">
		<div
			class="flex w-full cursor-pointer flex-row items-center justify-between gap-3 rounded-lg px-3 py-1 hover:bg-blue-950"
		>
			<div class="flex flex-row items-center gap-3">
				<!-- TODO: Customer Logo -->
				<img src="/favicon.png" class="h-6" />
				<div class="flex flex-col">
					<div class="text-lg/5 font-semibold">
						{{ myCustomer?.name ?? "Seedgreen" }}
					</div>
					<div class="text-sm text-gray-400">
						{{ myUser?.name }}
					</div>
				</div>
			</div>
			<div>
				<pwa-update-indicator class="-translate-y-1 translate-x-1" />
				<ion-icon :icon="caretDown" />
			</div>
		</div>
	</div>
	<ion-popover
		trigger="customer-badge"
		trigger-action="click"
		side="bottom"
		:dismiss-on-select="true"
		size="cover"
		:show-backdrop="false"
		:arrow="false"
	>
		<div class="flex w-full flex-col px-2 py-1.5">
			<template v-for="item in menuItems" :key="item.title">
				<template v-if="item.show ?? true">
					<!-- Dividers -->
					<template v-if="item.type === 'divider'">
						<div
							class="mt-1.5 w-full"
							:class="[item.title ? 'mb-0.5' : 'mb-1.5']"
						>
							<div class="w-full border-b border-gray-300" />
							<div
								v-if="item.title"
								class="pl-2 pt-1 text-sm text-gray-800"
							>
								{{ item.title }}
							</div>
						</div>
					</template>
					<!-- Menu Items -->
					<template v-else>
						<div
							class="w-full py-2"
							:class="[
								item.disabled
									? 'pointer-events-none cursor-not-allowed opacity-50'
									: 'cursor-pointer hover:bg-gray-100',
							]"
							@click="
								() => {
									item.onClick?.();
									emit('buttonClicked');
								}
							"
						>
							<div
								class="flex items-center gap-3 text-gray-800"
								:class="{
									'font-bold': item.showUpdate,
								}"
							>
								<ion-icon
									v-if="item.ionIcon"
									:icon="item.ionIcon"
									class="text-[1.5rem]"
								/>
								<div>{{ item.title }}</div>
								<pwa-update-indicator
									:is-visible="item.showUpdate === true"
									class="-translate-y-1 translate-x-1"
								/>
							</div>
						</div>
					</template>
				</template>
			</template>
		</div>
	</ion-popover>

	<InviteUserModal ref="inviteUserModal" />

	<CustomerSelect ref="customerSelect"><div /></CustomerSelect>
</template>

<script setup lang="ts">
	import useLogout from "@/composables/useLogout";
	import { IonIcon, IonPopover, useIonRouter } from "@ionic/vue";
	import {
		caretDown,
		cogOutline,
		downloadOutline,
		fingerPrintOutline,
		logOutOutline,
		peopleOutline,
		personAddOutline,
		shuffleOutline,
		syncOutline,
		idCardOutline,
		businessOutline,
	} from "ionicons/icons";
	import { useMyCustomer } from "seedgreen-shared/queries/customer";
	import { useMyUser } from "seedgreen-shared/queries/user";
	import { computed, inject, ref } from "vue";
	import CustomerSelect from "@/components/feature/CustomerSelect.vue";
	import InviteUserModal from "@/components/feature/auth/invite/InviteUserModal/InviteUserModal.vue";
	import PwaUpdateIndicator from "@/components/ui/PwaUpdateIndicator.vue";
	import { PermissionKey } from "seedgreen-shared/dtos/PermissionTypeDTO";
	import { useShowInstallButton } from "@/composables/useShowInstallButton";

	import { usePWAUpdate } from "@/composables/usePwaUpdate";

	import { useI18n } from "vue-i18n";
	const showInstallModal = inject("showInstallModal");

	const { myUser } = useMyUser();
	const { myCustomer } = useMyCustomer();
	const { logoutWithConfirmation } = useLogout();
	const showInstallButton = useShowInstallButton();

	const customerSelect = ref<InstanceType<typeof CustomerSelect>>();
	const inviteUserModal = ref<InstanceType<typeof InviteUserModal>>();

	const router = useIonRouter();
	const { updateAvailable, refreshPWA: refreshPwaApp } = usePWAUpdate();
	const { t } = useI18n();

	const emit = defineEmits(["buttonClicked"]);

	interface MenuItem {
		type: "item";
		title: string;
		ionIcon?: string;
		onClick?: () => void;
		show?: boolean;
		disabled?: boolean;
		showUpdate?: boolean;
	}
	interface DividerItem {
		type: "divider";
		title?: string;
		show?: boolean;
	}
	type DropdownMenuItem = MenuItem | DividerItem;
	const menuItems = computed<DropdownMenuItem[]>(() => [
		{
			type: "item",
			title: t("sidemenu-dropdown.preferences"),
			ionIcon: cogOutline,
			show: false,
		},
		{
			type: "divider",
			title: t("sidemenu-dropdown.admin"),
			show: myUser.value?.hasAnyPermission(
				PermissionKey.ManageUserAccounts,
				PermissionKey.AssignPermissions,
			),
		},
		{
			type: "item",
			title: t("sidemenu-dropdown.user-management"),
			ionIcon: peopleOutline,
			show: myUser.value?.hasAnyPermission(
				PermissionKey.ManageUserAccounts,
				PermissionKey.AssignPermissions,
			),
			onClick: () => {
				router.push("/settings/users");
			},
		},
		{
			type: "item",
			title: "Role Management",
			ionIcon: idCardOutline,
			show: myUser.value?.hasAnyPermission(
				PermissionKey.ManageUserAccounts,
				PermissionKey.AssignPermissions,
			),
			onClick: () => {
				router.push("/settings/roles");
			},
		},
		{
			type: "item",
			title: "Customer Settings",
			ionIcon: businessOutline,
			show: myUser.value?.admin === true,
			onClick: () => {
				router.push("/settings/customer");
			},
		},
		{
			type: "item",
			title: t("sidemenu-dropdown.invite-user"),
			ionIcon: personAddOutline,
			show: myUser.value?.hasAnyPermission(
				PermissionKey.ManageUserAccounts,
			),
			onClick: () => {
				inviteUserModal.value?.open();
			},
		},
		{
			type: "divider",
			title: t("sidemenu-dropdown.internal-user"),
			show: myUser.value?.internalUser,
		},
		{
			type: "item",
			title: t("sidemenu-dropdown.internal-dashboard"),
			ionIcon: cogOutline,
			show: myUser.value?.internalUser,
			onClick: () => {
				router.push("/internal");
			},
		},
		{
			type: "item",
			title: t("sidemenu-dropdown.change-customer"),
			ionIcon: shuffleOutline,
			show: myUser.value?.internalUser,
			onClick: () => {
				customerSelect.value?.open();
			},
		},
		{
			type: "divider",
			show: showInstallButton.value,
		},
		{
			type: "item",
			title: t("sidemenu-dropdown.setup-device-login"),
			ionIcon: fingerPrintOutline,
			show: false,
		},
		{
			type: "item",
			title: t("pwa.install-app"),
			ionIcon: downloadOutline,
			show: showInstallButton.value,
			onClick: showInstallModal as () => void,
		},
		{
			type: "divider",
		},
		{
			type: "item",
			title: t("sidemenu-dropdown.update-available"),
			ionIcon: syncOutline,
			show: updateAvailable.value,
			showUpdate: true,
			onClick: refreshPwaApp,
		},
		{
			type: "item",
			title: t("common.logout"),
			ionIcon: logOutOutline,
			onClick: logoutWithConfirmation,
		},
	]);
</script>
